import { useServices } from '@/app/providers/ServiceContext';
import { useToast } from '@/app/providers/ToastContext';
import type { AccessDoor } from '@yawi/types';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useAccessDoors(tenantId: string) {
  const { accessDoorRepository } = useServices();
  const { showToast } = useToast();

  const [doors, setDoors] = useState<AccessDoor[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Solo la última petición escribe el estado (respuestas fuera de orden).
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await accessDoorRepository.listDoors({
        page,
        limit: pageSize,
        search: search || undefined,
        tenantId,
      });
      if (requestId !== requestIdRef.current) return;
      setDoors(res.data);
      setTotal(res.meta?.total ?? res.data.length);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      const msg = e instanceof Error ? e.message : 'Error al cargar puertas';
      setError(msg);
      showToast('error', msg);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [accessDoorRepository, page, pageSize, search, tenantId, showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { doors, total, page, pageSize, search, loading, error, setPage, setSearch, refresh };
}
